import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { useTheme } from '../../../theme/ThemeContext';
import useRightEdgePanelStore from '../../../stores/rightEdgePanelStore';

/**
 * RoleTaskBadge — shared shell for role-aware "tasks" widgets.
 *
 * Mirrors the doctor's ProcedureHalfDiscWidget pattern (small teal pill
 * on the right edge → hover or click expands to a full panel) but with
 * a generic content slot so receptionist / nurse / junior-doctor / lab
 * widgets can share the same visual + interaction layer.
 *
 * Props:
 *   count       — number badge (visit/sitting count for the role)
 *   countLabel  — short uppercase suffix under the count, e.g. "VISITS",
 *                 "TASKS"
 *   title       — header title in open mode (e.g. "Action pending")
 *   subtitle    — optional small line rendered under the title (e.g.
 *                 "💰 3 · 📤 2" sub-counts).
 *   children    — content rendered in the open panel body
 *   ariaLabel   — accessibility label for the badge
 *   tone        — optional accent ('teal' default, 'amber', 'rose')
 *   verticalAnchor — 'center' (default) or px from top
 *
 * Closed (badge): teal 64px circle, count + label only.
 * Hover/click: expands to a 380px panel anchored just below the app
 *   header so it stretches near full viewport height.
 */
const TONE_FILL = {
  teal:   '#0d9488',
  amber:  '#d97706',
  rose:   '#e11d48',
  blue:   '#2563eb',
  purple: '#a855f7',  // Appointments accent (Confirm Appointments badge)
};

export default function RoleTaskBadge({
  count = 0,
  countLabel = 'TASKS',
  title = 'Tasks',
  subtitle,                     // optional: small meta line under title
  children,
  footer,                       // optional: sticky footer slot rendered below body
  ariaLabel,
  tone = 'teal',
  verticalAnchorPx,             // optional: stacks multiple widgets
  initialOpen = false,
  hideWhenEmpty = true,         // Phase 4.0d — render nothing at count 0
  metricKey,                    // Phase 4.0d — telemetry axis label
}) {
  const { isDark } = useTheme();
  const widgetRef = useRef(null);
  const [open, setOpen] = useState(initialOpen);
  const hoverLeaveTimer = useRef(null);
  const panelKey = useId();

  const palette = {
    bg:      isDark ? '#0f172a' : '#ffffff',
    bgAlt:   isDark ? '#1e293b' : '#f8fafc',
    border:  isDark ? 'rgba(255,255,255,0.10)' : 'rgba(15,23,42,0.12)',
    text:    isDark ? '#e2e8f0' : '#1e293b',
    muted:   isDark ? '#94a3b8' : '#64748b',
    accent:  TONE_FILL[tone] || TONE_FILL.teal,
  };

  // Phase 4.0g — switch from hover-leave-with-timer to click-outside.
  // Mouse-leave was collapsing the bubble whenever the cursor briefly
  // exited the panel bounds while clicking an interior control (e.g.
  // an AssigneePicker avatar near the edge), making it impossible to
  // pick anything. New rule: hover or click opens the bubble; only a
  // genuine click outside the bubble closes it.
  const handleMouseEnter = useCallback(() => {
    if (!open) setOpen(true);
  }, [open]);

  // Click-outside listener. We listen on `mouseup` (not mousedown) and
  // additionally remember the mousedown target so we can ignore
  // "drag-out" gestures — e.g. dragging a textarea resize grip inside
  // a child dialog past the panel boundary, where mousedown lands on
  // the textarea and mouseup lands on something outside the widget
  // tree. Treating that as "click outside" used to collapse the bubble
  // mid-drag.
  useEffect(() => {
    if (!open) return undefined;
    let downInside = false;
    const isInsideKeepZone = (target) => {
      const node = widgetRef.current;
      if (node && node.contains(target)) return true;
      // Modal dialogs (e.g. ProcedureNoteDialog) render at root level;
      // they tag themselves with `role="dialog"` or
      // `data-keep-bubble-open="1"` so we keep the bubble open while
      // the user interacts with them.
      return !!target?.closest?.(
        '[role="dialog"], [data-keep-bubble-open="1"]',
      );
    };
    const onDown = (e) => {
      downInside = isInsideKeepZone(e.target);
    };
    const onUp = (e) => {
      // Drags that *started* inside should never close the bubble —
      // even if mouseup lands somewhere external (cursor exiting the
      // dialog while dragging the textarea resize handle, scrollbar,
      // etc.).
      if (downInside) {
        downInside = false;
        return;
      }
      if (isInsideKeepZone(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('touchstart', onDown, true);
    document.addEventListener('mouseup', onUp, true);
    document.addEventListener('touchend', onUp, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('touchstart', onDown, true);
      document.removeEventListener('mouseup', onUp, true);
      document.removeEventListener('touchend', onUp, true);
    };
  }, [open]);

  // Cleanup the legacy hover timer if any still in flight.
  useEffect(() => () => {
    if (hoverLeaveTimer.current) {
      clearTimeout(hoverLeaveTimer.current);
      hoverLeaveTimer.current = null;
    }
  }, []);

  // Telemetry — fire once when the pill transitions from closed to open.
  // Caller passes a stable axis identifier (e.g. 'mytasks', 'delegated',
  // 'action_pending'); we silently no-op if none is supplied.
  useEffect(() => {
    if (open && metricKey) {
      // eslint-disable-next-line no-console
      console.info(`metric=${metricKey}_open count=${count}`);
    }
  }, [open, metricKey, count]);

  // Publish open-state to the shared right-edge signal so the per-visit
  // ProcedureWidget disc hides itself instead of painting on top of this
  // panel (which would occlude the card cost text). Reset on unmount so a
  // closing/unmounting panel never leaves the disc suppressed.
  const setOpenPanel = useRightEdgePanelStore((s) => s.setOpenPanel);
  const openPanelKey = useRightEdgePanelStore((s) => s.openPanelKey);
  useEffect(() => {
    setOpenPanel(panelKey, open);
    return () => setOpenPanel(panelKey, false);
  }, [open, panelKey, setOpenPanel]);

  // Another right-edge badge's panel is open — step aside so two badges never
  // overlap on the right gutter (the open panel spans the full right strip).
  const otherPanelOpen = openPanelKey != null && openPanelKey !== panelKey;

  // Auto-hide at zero count (placed AFTER all hooks per React's rules
  // of hooks). Keeps the right edge clean when a role has no actionable
  // work and lets sibling pills reflow visually. But: if the user has
  // already opened the panel, keep it mounted through a transient
  // count→0 — otherwise taking the last payment unmounts the badge and
  // the next sitting (arriving via WS milliseconds later) remounts it
  // CLOSED, forcing a re-click. The empty body already shows the
  // "✅ All clear" message in this case.
  if (hideWhenEmpty && count === 0 && !open) return null;

  // While another right-edge panel is open, hide this one's collapsed bubble
  // (it would otherwise paint on top of the sibling's panel).
  if (!open && otherPanelOpen) return null;

  const width = open ? 380 : 64;
  // Closed: vertically centered on the right edge.
  // Open (unanchored): start just below the app header so the panel
  // stretches near full viewport height instead of being penned to the
  // bottom half by a 50% top anchor.
  const top = verticalAnchorPx != null
    ? { top: verticalAnchorPx, transform: undefined }
    : open
      ? { top: '4.5rem', transform: undefined }
      : { top: '50%', transform: 'translateY(-50%)' };

  // When the caller anchors the widget to a specific top px, we cap
  // the open-mode height instead of stretching to the bottom of the
  // viewport. This lets two widgets (e.g. doctor's per-visit disc +
  // their cross-visit "My tasks" pill) coexist without overlapping.
  const openSizing = verticalAnchorPx != null
    ? { maxHeight: '60vh' }
    : { bottom: '1rem', maxHeight: 'calc(100vh - 6.5rem)' };

  return (
    <aside
      ref={widgetRef}
      aria-label={ariaLabel || `${title} (${count})`}
      onMouseEnter={handleMouseEnter}
      onClick={!open ? () => setOpen(true) : undefined}
      style={{
        position: 'fixed',
        right: open ? 0 : '0.75rem',
        ...top,
        ...(open ? openSizing : { height: 64 }),
        width,
        minWidth: width,
        maxWidth: width,
        zIndex: 30,
        transition: 'width 150ms ease-out, height 150ms ease-out',
        background: open ? palette.bg : palette.accent,
        color: open ? palette.text : '#ffffff',
        cursor: open ? 'default' : 'pointer',
        border: open ? `1px solid ${palette.border}` : 'none',
        borderRight: open ? 'none' : undefined,
        borderTopLeftRadius: open ? 24 : 18,
        borderBottomLeftRadius: open ? 24 : 18,
        borderTopRightRadius: open ? 0 : 18,
        borderBottomRightRadius: open ? 0 : 18,
        boxShadow: open
          ? (isDark
              ? '-12px 8px 28px rgba(0,0,0,0.45)'
              : '-12px 8px 28px rgba(15,23,42,0.12)')
          : `0 6px 18px ${palette.accent}80, 0 2px 6px rgba(0,0,0,0.15)`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: open ? 'stretch' : 'center',
        justifyContent: open ? 'flex-start' : 'center',
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      }}
    >
      {!open && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            userSelect: 'none',
          }}
        >
          <span style={{ fontSize: count > 99 ? '1.25rem' : '1.5rem', fontWeight: 700, lineHeight: 1 }}>
            {count}
          </span>
          <span style={{ marginTop: 2, fontSize: '0.5625rem', opacity: 0.85, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            {countLabel}
          </span>
        </div>
      )}

      {open && (
        <>
          <header
            style={{
              padding: '0.75rem 1rem',
              borderBottom: `1px solid ${palette.border}`,
              background: palette.bgAlt,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.5rem',
              flexShrink: 0,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: 8,
                background: `${palette.accent}22`,
                color: palette.accent,
                fontSize: '0.875rem',
                fontWeight: 700,
              }}
            >
              {count}
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                {title}
              </span>
              {subtitle && (
                <span style={{ fontSize: '0.6875rem', color: palette.muted, marginTop: 1 }}>
                  {subtitle}
                </span>
              )}
            </span>
          </header>
          <div
            style={{
              flex: 1,
              padding: '0.75rem 1rem',
              overflowY: 'auto',
              fontSize: '0.8125rem',
              color: palette.text,
              display: 'flex',
              flexDirection: 'column',
              gap: '0.625rem',
              minHeight: 0,
            }}
          >
            {typeof children === 'function' ? children({ palette }) : children}
          </div>
          {footer && (
            <div
              style={{
                flexShrink: 0,
                borderTop: `1px solid ${palette.border}`,
                background: palette.bgAlt,
                padding: '0.625rem 0.75rem',
              }}
            >
              {typeof footer === 'function' ? footer({ palette }) : footer}
            </div>
          )}
        </>
      )}
    </aside>
  );
}
